<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';
import { useStore } from 'vuex';
import { off, postEvent, type PopupButton  } from '@tma.js/sdk-vue'
import User from '@/models/user';
import { setBackButton, hideBackButton, setBottomButton, hideBottomButton, setBottomSecondaryButton, hideBottomSecondaryButton } from '@/tools/setTelegramButtons';
import { addUser, editUser, delUser } from '@/composables/userWorker';
import { createPopupManager } from '@/tools/setTelegramPopUp'
import { setHaptic } from '@/tools/setTelegramHaptick';

const props = defineProps<{ 
  editworker?: boolean,
  adminToEdit: User | null
}>();

console.log('props.adminToEdit=', props.adminToEdit)

// Roles configuration
const ROLES = [
  { value: 'cass', label: 'Кассир' },
  { value: 'loader', label: 'Грузчик' },
  { value: 'warehouse', label: 'Склад' },
  { value: 'driver', label: 'Водитель' },
  { value: 'admin', label: 'Администратор' },
  { value: 'zagotovshik', label: 'Заготовщик' },
  { value: 'warehouse_driver_loader', label: 'Склад, водитель, грузчик' }
] as const;

type RoleValue = typeof ROLES[number]['value'];

const roleMap: Record<string, string> = {
  cass: 'Кассир',
  loader: 'Грузчик',
  warehouse: 'Склад',
  driver: 'Водитель',
  admin: 'Администратор',
  zagotovshik: 'Заготовщик',
  warehouse_driver_loader: 'Склад, водитель, грузчик'
};


const popupManager = createPopupManager();
const emit = defineEmits(['close'])
const store = useStore();
const role = ref(props.adminToEdit?.role ?? 'cass');
const roleTransl = ref(roleMap[role.value]);

let mainButtonListener: (() => void) | null = null;
let secondaryButtonListener: (() => void) | null = null;

const handleMainButton = () => {
  console.log('Нажата основная кнопка');
  if (props.editworker) {
    editWorker();
  }
  else {
    openShare();
  }
};

const handleSecondaryButton = () => {
  console.log('Нажата вторичная кнопка');
  if (props.editworker) {
    let buttons: PopupButton[];
    buttons = [
      {
        id: 'delete',
        text: 'Удалить',
        type: 'destructive'
      },
      {
        id: 'cancel',
        type: 'close'
      }
    ];
    popupManager.openPopup(`Вы уверены что хотите удалить сотрудника?`,
      (buttonId) => {
        if (buttonId === 'cancel') {
          popupManager.cleanup();
        }
        if (buttonId === 'delete') {
          delWorker();
          popupManager.cleanup();
        }
      }, buttons
    );
  }
  else {
    emit('close');
  }
};

onMounted(async () => {
  setBackButton();
  mainButtonListener = handleMainButton;
  secondaryButtonListener = handleSecondaryButton;
  setBottomButton(props.editworker ? 'ПОДТВЕРДИТЬ' : 'ПРИГЛАСИТЬ', 'F8A917', 'FFFFFF', handleMainButton)
  setBottomSecondaryButton(props.editworker ? 'УДАЛИТЬ' : 'ОТМЕНА', 'E64D44', 'FFFFFF', 'right', handleSecondaryButton);
})

onUnmounted(() => {
  hideBottomButton();
  hideBottomSecondaryButton();
});

//Логика изменения сотрудника
async function editWorker(){
  setHaptic();
  hideBottomSecondaryButton();
  setBottomButton('Ждём подтверждения', '31B545', 'FFFFFF', null, false, true);
  if (props.adminToEdit?.telegram_id !== undefined) {
    await editUser(props.adminToEdit?.id, role.value);
    popupManager.openPopup(`Вы успешно изменили роль ${props.adminToEdit?.username} с ${roleMap[props.adminToEdit?.role]} на ${roleMap[role.value]}`,
      (buttonId) => {
        if (buttonId === 'cancel') {
          popupManager.cleanup();
          emit('close');
        }
      }
    );
  } else {
    console.error('Error: adminToEdit does not have a valid telegram_id');
  }
}

//Логика удаления сотрудника
async function delWorker() {
  setBottomButton('Ждём подтверждения', '31B545', 'FFFFFF', null, false, true);
  hideBottomSecondaryButton();
  if (props.adminToEdit?.telegram_id !== undefined) {
    await delUser(props.adminToEdit?.id);
    popupManager.openPopup(`Вы успешно удалили пользователя ${props.adminToEdit?.username}`,
    (buttonId) => {
      if (buttonId === 'cancel') {
        popupManager.cleanup();
        emit('close');
        }
      },
    );
  }
}

//Логика добавления новых людей

function selectRole(roleSelect: RoleValue) {
  role.value = roleSelect;
  roleTransl.value = roleMap[roleSelect];
}

async function openShare() {
  setHaptic();
  roleTransl.value = roleMap[role.value];
  await addUser(store.getters.USER.telegram_id, role.value);
  const inviteText = `Привет!\nВыдал тебе доступ ${roleTransl.value}`;
  postEvent('web_app_open_tg_link', {
    path_full: `/share/url?text=${encodeURIComponent(inviteText)}&url=${encodeURIComponent(`https://t.me/chpx_opt_bot/chpx?startapp=${store.getters.USER.telegram_id}_${role.value}`)}`
  });
  emit('close');
}
</script>

<template>
  <div class="add-admin-wrapper-popup">
    <div class="close-btn-wrapper">
      <p class="popup-label">{{ props.editworker ? `Редактировать сотрудника ${props.adminToEdit?.username}` : 'Добавить сотрудника' }}</p>
      <button type="button" class="btn">
        <img src="@/assets/desktop/close_cart.svg" class="close-img" @click="emit('close')">
      </button>
    </div>
    <div class="popup-roles">
      <p 
        :style="{ 
          color: props.adminToEdit ? 'var(--green-color)' : 'black', 
          fontWeight: props.adminToEdit ? '600' : '',  
        }">{{ props.editworker ? props.adminToEdit?.first_name : 'Выберите роль:' }}</p>
      <div class="roles-wrapper">
        <div 
          v-for="(r, i) in ROLES" 
          :key="r.value"
          class="role" 
          @click="selectRole(r.value)"
          :style="{'box-shadow': i !== ROLES.length - 1 ? '0 0.5px 0 #C8C7CB' : ''}"
        >
          <p class="role-text">{{ r.label }}</p>
          <img v-if="role === r.value" class="role-checker" src="@/assets/check.svg">
          <img v-else class="role-checker" src="@/assets/uncheck.svg">
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>

.close-img{
  width: 30px;
}

.btn {
  border: none;
  outline: none;
  cursor: pointer;
  font-family: inherit;
  flex-shrink: 0;
  background: 0;
}

.btn:active {
  transform: var(--transform-scale-active);
}

.btn-close{
  text-align: center;
  color: white;
  font-size: 18px;
  font-weight: 600;
  background-color: var(--red-color);
  padding: 11px 11px;
  border-radius: var(--border-radius);
  width: 100%;
}

.close-btn-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.btns {
  margin-top: 30px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 90%;
}

.roles-wrapper{
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: white;
  border-radius: var(--border-radius);
  margin-top: 20px;
}

.role-text {
  font-size: 18px;
}

.role-checker {
  width: 25px;
  display: flex;
}

.role {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 20px 0;
}

.popup-roles {
  width: 100%;
  text-align: start;
  font-size: 16px;
  margin-top: 20px;
}

.popup-label {
  text-align: start;
  font-size: 18px;
  font-weight: 500;
}

.add-admin-wrapper-popup{
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100vw;
  background-color: white;
  border-top-left-radius: var(--border-radius);
  border-top-right-radius: var(--border-radius);
  padding: 20px;
  box-sizing: border-box;
  z-index: 2;
}

</style>
